// English strings for the console batch 4 (SYSTEM-001 / AUDIT-001 / RISK-001 / ORG-001, owner 2026-10-25;
// D-067): the console team and its roles, the audit log, shoppers' abuse reports with the storefront
// take-down, legal document versions and who accepted them, and the root AI and background-job
// monitor. Spread into `en` as `en.trustOps`, so keys read `t("trustOps.…")`.
export const trustOpsStrings = {
  nav: { reports: "Abuse reports" },
  sections: {
    reports: "What shoppers reported about shops, and storefronts Adyvance paused.",
  },
  security: {
    tabs: { team: "Console team", audit: "Audit log" },
  },
  team: {
    lead: "People who can sign in to this console. Each needs an Adyvance account with two-factor sign-in set up.",
    roles: {
      owner: { name: "Owner", help: "Everything, including the team." },
      operator: { name: "Operator", help: "Shops, money, switches and settings." },
      support: { name: "Support", help: "Help requests and shop access when a shop allows it." },
      reviewer: { name: "Reviewer", help: "Identity checks and reports." },
    },
    columns: {
      person: "Person",
      role: "Role",
      twoFactor: "Two-factor",
      lastSeen: "Last seen",
      active: "Can sign in",
    },
    you: "You",
    mfaOn: "Set up",
    mfaOff: "Not set up",
    mfaOffHelp: "They cannot sign in to the console until they set up two-factor sign-in.",
    never: "Never",
    add: "Add a person",
    email: "Their Adyvance account email",
    emailPh: "name@example.com",
    role: "Role",
    addDo: "Add to team",
    added: "Added to the console team",
    saved: "Team updated",
    onlyOwner: "Only an owner can change the team.",
    errors: {
      no_account: "No Adyvance account uses this email. They need to sign up first.",
      already: "This person is already on the team.",
      self: "You cannot change your own role or turn yourself off.",
      last_owner: "The console needs at least one active owner.",
    },
  },
  audit: {
    lead: "Every recorded change, newest first: what the console team did and what happened in shops.",
    action: "Action contains",
    actionPh: "e.g. plan, wallet, storefront",
    shop: "Shop",
    anyShop: "All shops",
    search: "Search",
    clear: "Clear",
    columns: { when: "When", who: "Who", action: "Action", shop: "Shop", target: "On" },
    actors: {
      platform_staff: "Console team",
      user: "Shop user",
      system: "System",
      api_key: "API key",
      customer: "Shopper",
    },
    details: "Details",
    loadMore: "Show older",
    none: "Nothing matches.",
    end: "That is everything.",
  },
  reports: {
    lead: "Shoppers report a shop from the “Report this shop” link at the bottom of every storefront. Review each report, warn the shop, or pause its storefront.",
    states: {
      new: "New",
      reviewing: "Reviewing",
      actioned: "Action taken",
      dismissed: "Dismissed",
    },
    reasons: {
      scam: "Scam or fraud",
      counterfeit: "Fake or counterfeit goods",
      prohibited: "Banned or illegal goods",
      not_delivered: "Paid but not delivered",
      abusive: "Abusive or offensive",
      other: "Something else",
    },
    filters: { open: "Open", all: "All" },
    page: "Page",
    contact: "Reporter contact",
    noContact: "Left no contact",
    shopReports: {
      one: "{count} report about this shop in 90 days",
      other: "{count} reports about this shop in 90 days",
    },
    paused: "Storefront paused",
    note: "Note (only the console team sees it)",
    move: "Mark as",
    save: "Save",
    saved: "Report updated",
    warn: "Warn the shop",
    warnTitle: "Notice title",
    warnTitleDefault: "A shopper reported your shop",
    warnBody: "What the shop should fix",
    warnDo: "Send warning",
    warned: "Warning sent to the shop",
    openShop: "Open shop",
    none: "No reports here.",
    pausedList: "Paused storefronts",
    pausedNone: "No storefront is paused.",
    pausedSince: "Paused {date}",
  },
  storefront: {
    pause: "Pause storefront",
    resume: "Open storefront",
    pauseTitle: "Pause {name}'s storefront?",
    pauseBody:
      "Shoppers see “This store is not available” on every page and domain. The shop's admin keeps working and the shop gets a notice with your reason.",
    resumeTitle: "Open {name}'s storefront again?",
    resumeBody: "The storefront comes back on its domains within a minute. The shop gets a notice.",
    reason: "Reason (the shop sees it)",
    reasonPh: "e.g. several shoppers reported paid orders that were never delivered",
    resumeReasonPh: "e.g. the shop refunded the reported orders",
    paused: "Storefront paused",
    resumed: "Storefront open again",
    pausedBanner: "Adyvance paused this shop's storefront {date}: {reason}",
    cancel: "Cancel",
  },
  legal: {
    tabs: { switches: "Switches", legal: "Legal documents" },
    lead: "Publish a new version of the terms or the privacy notice. From its start date every shop owner is asked to accept it before using the admin.",
    kinds: { terms: "Terms of service", privacy: "Privacy notice" },
    current: "In effect",
    next: "Starts {date}",
    noneYet: "Nothing published yet. Shops are not asked to accept anything.",
    version: "Version",
    versionPh: "e.g. 2026-11",
    accepted: { one: "Accepted by {count} person", other: "Accepted by {count} people" },
    history: "Earlier versions",
    open: "Open document",
    publish: "Publish a version",
    kind: "Document",
    url: "Link to the document (https)",
    urlPh: "https://adyvance.com/legal/terms-2026-11",
    sha: "SHA-256 of the file (optional)",
    shaPh: "64 hex characters",
    summary: "What changed (shops see it)",
    summaryPh: "A short summary of the changes…",
    startsAt: "Takes effect",
    startsNow: "Now",
    publishDo: "Publish",
    published: "Version published",
    confirm: "Every shop owner will be asked to accept this before they can keep using the admin.",
    shops: "Shops",
    columns: { shop: "Shop", owner: "Owner", terms: "Terms", privacy: "Privacy" },
    upToDate: "Up to date",
    waiting: "Waiting",
    notAccepted: "Not accepted",
    filters: { all: "All shops", waiting: "Waiting to accept" },
    errors: { https: "Use an https link.", taken: "This version already exists." },
    pending: "Counsel's texts are not ready yet (OD-23); publish only approved versions.",
  },
  usage: {
    tabs: { ai: "root AI", jobs: "Background jobs" },
    aiLead:
      "root AI credits used this month by each shop, and what they cost Adyvance. Plan credits are counted each night (00:20), so today's use shows tomorrow; bought credits are live.",
    credits: "Credits used this month",
    cost: "Cost to Adyvance",
    packs: "AI packs sold this month",
    costPerCredit: "Cost of one credit",
    costPh: "Not set",
    costUnit: "paisa",
    costHelp: "Your Workers AI cost per credit, in paisa. Costs show once it is set.",
    costSave: "Save",
    costSaved: "Cost saved",
    columns: {
      shop: "Shop",
      plan: "Plan credits used",
      packs: "Bought credits used",
      balance: "Bought credits left",
      cost: "Cost",
    },
    none: "No shop used root AI this month.",
    notSet: "—",
  },
  jobs: {
    lead: "Background work that failed after all its retries, and the schedules that run on their own.",
    failures: "Failed jobs",
    states: { open: "Open", retried: "Retried", discarded: "Discarded" },
    columns: { job: "Job", shop: "Shop", error: "Error", tries: "Tries", when: "Failed" },
    attempts: { one: "{count} try", other: "{count} tries" },
    retry: "Retry",
    retried: "Sent again",
    discard: "Discard",
    discardReason: "Why discard it",
    discardDo: "Discard job",
    discarded: "Job discarded",
    noFailures: "No failed jobs here.",
    platform: "Platform",
    schedules: "Schedules",
    columns2: {
      key: "Schedule",
      every: "Runs",
      last: "Last run",
      next: "Next run",
      result: "Last result",
    },
    late: "Late",
    disabled: "Off",
    openFailures: { one: "{count} failed job waiting", other: "{count} failed jobs waiting" },
  },
} as const;
